import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import ApiClient from '@lib/apiClient';

import { validateFloatNumber, validateNumber } from '@utils/Util';

const CLOSE_ICON = `${process.env.PUBLIC_URL}/images/icons/close-icon.svg`;

const AllStyles = ({ onClose, onProjectDetailsCallBack }) => {
	const [orderQuotes, setOrderQuotes] = useState([]);
	const [editableField, setEditableField] = useState('');
	const [updatedData, setUpdatedData] = useState([]);
	const [editablePriceFieldId, setEditablePriceFieldId] = useState('');
	const [updatePriceData, setUpdatePriceData] = useState([]);
	const [errors, setErrors] = useState({
		priceError: '',
		productionCostError: ''
	});
	const [loading, setLoading] = useState(false);

	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();

	const getOrderQuotes = async (orderId) => {
		setLoading(true);
		await ApiClient.GET('getOrderQuotes', +orderId)
			.then(({ data }) => {
				setLoading(false);
				setOrderQuotes(data);
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	useEffect(() => {
		getOrderQuotes(id);
	}, [id]);

	const getTotal = (items) => {
		return items.reduce((total, pair) => {
			if (pair.quantity) {
				return total + parseInt(pair.quantity);
			}
			return total;
		}, 0);
	};

	const onEditQuantity = (item) => {
		setEditableField(item.id);
	};

	const resetValues = () => {
		setEditableField('');
		setUpdatedData([]);
		setUpdatePriceData([]);
		setEditablePriceFieldId('');
	};

	const onSave = async () => {
		setLoading(true);
		let body = {
			colorWiseSizeQuantityPairList: updatedData,
			total: updatedData.quantity
		};

		if (body.colorWiseSizeQuantityPairList.length !== 0) {
			await ApiClient.PUT('updateRfqColorSize', body.colorWiseSizeQuantityPairList, editableField)
				.then(({ data }) => {
					setLoading(false);
					getOrderQuotes(id);
					onProjectDetailsCallBack();
					resetValues();
					if (data.success) {
						toastSuccess(data.message);
					} else {
						toastError(data.message);
					}
				})
				.catch(({ response }) => {
					setLoading(false);
					resetValues();
					if (response.data && response.data.message) {
						toastError(response.data.message);
					} else {
						toastError('Something went wrong! Please try again.');
					}
				});
		} else {
			setLoading(false);
			resetValues();
		}
	};

	const onCancel = () => {
		setEditableField('');
		setEditablePriceFieldId('');
	};

	const onEditChange = (e, index, quoteIndex, inputIndex) => {
		let { value } = e.target;
		const newData = [...orderQuotes];
		const orderData = newData[quoteIndex];
		orderData.colorWiseSizeQuantityPairList[index].sizeQuantityPairList[inputIndex].quantity = isNaN(
			parseInt(value)
		)
			? 0
			: value === ''
				? 0
				: parseInt(value);
		newData[quoteIndex] = orderData;
		newData[quoteIndex]['total'] = getItemTotal(newData[quoteIndex].colorWiseSizeQuantityPairList);
		setOrderQuotes(newData);
		setUpdatedData(newData[quoteIndex]);
	};

	const onEditPriceInfo = (item) => {
		setEditablePriceFieldId(item.id);
	};

	const onEditPrice = (e, quoteIndex) => {
		let { name, value } = e.target;
		setErrors({
			...errors,
			priceError: ''
		});
		const newData = [...orderQuotes];
		newData[quoteIndex][name] = value;
		newData[quoteIndex]['total'] = getItemTotal(newData[quoteIndex].colorWiseSizeQuantityPairList);
		setUpdatePriceData(newData[quoteIndex]);
	};

	const onSavePriceData = async () => {
		setLoading(true);
		if (updatePriceData.length !== 0) {
			let body = updatePriceData;
			if (body.price === '') {
				setErrors({
					...errors,
					priceError: 'Price is required'
				});
				setLoading(false);
				return;
			}
			if (body.currency === '') {
				setErrors({
					...errors,
					priceError: 'Currency type is required'
				});
				setLoading(false);
				return;
			}
			if (body.productionCost === '') {
				setErrors({
					...errors,
					productionCostError: 'Production cost is required'
				});
				setLoading(false);
				return;
			}
			if (body.productionCurrency === '') {
				setErrors({
					...errors,
					productionCostError: 'Currency type is required'
				});
				setLoading(false);
				return;
			}
			await ApiClient.PUT('sendRfqOffer', body, editablePriceFieldId)
				.then(({ data }) => {
					setLoading(false);
					getOrderQuotes(id);
					resetValues();
					if (data.success) {
						toastSuccess(data.message);
					} else {
						toastError(data.message);
					}
				})
				.catch(({ response }) => {
					setLoading(false);
					resetValues();
					if (response.data && response.data.message) {
						toastError(response.data.message);
					} else {
						toastError('Something went wrong! Please try again.');
					}
				});
		} else {
			setLoading(false);
			resetValues();
		}
	};

	const getItemTotal = (items) => {
		return items.reduce((total, item) => {
			return total + getTotal(item.sizeQuantityPairList);
		}, 0);
	};

	const onEditClick = (id) => {
		localStorage.setItem('fromRoute', location.pathname);
		navigate(`/designs/edit/${id}`);
	};
	const onDetailsClick = (id) => navigate(`/designs/view/${id}`);

	return (
		<LoaderComponent loading={loading}>
			<section className="all-style-popup-section">
				<div className="style-title-section">
					<div className="container-fluid">
						<div className="row">
							<div className="col-6">
								<h2>All styles</h2>
							</div>
							<div className="col-6 text-right">
								<img
									style={{ cursor: 'pointer' }}
									src={CLOSE_ICON}
									alt="CLose"
									onClick={onClose}
								/>
							</div>
						</div>
					</div>
				</div>

				{orderQuotes &&
					orderQuotes.map((item, quoteIndex) => (
						<div className="single-style-row" key={item.id}>
							<div className="container-fluid">
								<div className="row">
									<div className="col-5 d-flex pl-0">
										<div className="style-image mr-3">
											<img
												src={
													item.documentResponseList
														? item.documentResponseList[0].docUrl
														: ''
												}
												alt={item.name}
												onClick={() => onDetailsClick(item.productId)}
											/>
										</div>
										<div className="style-info">
											<Tooltip title={item.name} placement="top" arrow>
												<h3>{item.name}</h3>
											</Tooltip>

											<button onClick={() => onEditClick(item.productId)}>
												Edit design
											</button>
											<div className="units-info">
												{/* <h4>{item.quantity} units</h4> */}
												<h4>
													{getItemTotal(item.colorWiseSizeQuantityPairList)} units
												</h4>
												{/* <span>S-XL</span> */}
												<span>
													{item.colorWiseSizeQuantityPairList.length} colors
												</span>
											</div>
										</div>
									</div>
									<div className="col-4 text-right">
										<div className="quantity-table">
											<table>
												<tbody>
													<tr>
														<th></th>

														{item.colorWiseSizeQuantityPairList &&
															item.colorWiseSizeQuantityPairList[0]?.sizeQuantityPairList?.map(
																(style) => (
																	<th key={style.code}>{style.code}</th>
																)
															)}
														<th>Total</th>
													</tr>

													{item.colorWiseSizeQuantityPairList &&
														item.colorWiseSizeQuantityPairList.map(
															(value, index) => (
																<tr key={value.id}>
																	<td>
																		<span
																			className="color"
																			style={{
																				background: value.hexCode
																			}}
																		/>
																	</td>

																	{value &&
																		value?.sizeQuantityPairList.map(
																			(style, inputIndex) => (
																				<td key={style.code}>
																					{editableField ==
																					item.id ? (
																						<input
																							type="text"
																							autoComplete="off"
																							name={style.code}
																							value={
																								style.quantity
																							}
																							placeholder="00"
																							onChange={(e) =>
																								onEditChange(
																									e,
																									index,
																									quoteIndex,
																									inputIndex
																								)
																							}
																							onKeyPress={
																								validateNumber
																							}
																						/>
																					) : (
																						<p>
																							{style.quantity
																								? style.quantity
																								: 0}
																						</p>
																					)}
																				</td>
																			)
																		)}
																	<td>
																		<p>
																			{getTotal(
																				value.sizeQuantityPairList
																			)}
																		</p>
																	</td>
																</tr>
															)
														)}
												</tbody>
											</table>
										</div>

										{editableField == item.id ? (
											<>
												<button
													className="cancel-button"
													onClick={() => onCancel(item)}
												>
													Cancel
												</button>
												<button
													className="save-button inactive"
													onClick={() => onSave(item)}
												>
													Save
												</button>
											</>
										) : (
											<button
												className="edit-button"
												onClick={() => onEditQuantity(item)}
											>
												Edit quantity
											</button>
										)}
									</div>
									<div className="col-3">
										<div className="price-column text-center">
											{editablePriceFieldId == item.id ? (
												<div className="edit-view text-left">
													<p className="pb-1 mb-0">Factory cost</p>
													<div className="form-group d-flex mb-2">
														<input
															type="text"
															autoComplete="off"
															id="productionCost"
															name="productionCost"
															value={item.productionCost}
															onChange={(e) => onEditPrice(e, quoteIndex)}
															onKeyPress={validateFloatNumber}
															placeholder="0"
														/>

														<select
															id="productionCurrency"
															name="productionCurrency"
															onChange={(e) => onEditPrice(e, quoteIndex)}
															value={item.productionCurrency}
														>
															<option value="" defaultValue></option>
															<option value="USD">$</option>
															<option value="EUR">€</option>
														</select>
													</div>
													{errors.productionCostError ? (
														<p className="error">{errors.productionCostError}</p>
													) : (
														<></>
													)}

													<p className="pb-1 mb-0 mt-2">Buyer cost</p>
													<div className="form-group d-flex mb-0">
														<input
															type="text"
															autoComplete="off"
															id="price"
															name="price"
															value={item.price}
															onChange={(e) => onEditPrice(e, quoteIndex)}
															onKeyPress={validateFloatNumber}
															placeholder="0"
														/>

														<select
															id="currency"
															name="currency"
															onChange={(e) => onEditPrice(e, quoteIndex)}
															value={item.currency}
														>
															<option value="USD">$</option>
															<option value="EUR">€</option>
														</select>
													</div>
													{errors.priceError ? (
														<p className="error">{errors.priceError}</p>
													) : (
														<></>
													)}
													<p className="pb-0 total-value-info mb-2">
														Total value:{' '}
														{item.price *
															getItemTotal(item.colorWiseSizeQuantityPairList)}
													</p>
													<p className="price-type mb-0">Price type</p>
													<input
														type="radio"
														id="priceType"
														name="priceType"
														value="FOB"
														checked={item.priceType === 'FOB'}
														onChange={(e) => onEditPrice(e, quoteIndex)}
													/>
													<label htmlFor="fob">Fob</label>
													<input
														type="radio"
														id="priceType"
														name="priceType"
														value="CIF"
														checked={item.priceType === 'CIF'}
														onChange={(e) => onEditPrice(e, quoteIndex)}
													/>
													<label htmlFor="cif">CIF</label>
													<div className="actions text-center pt-2">
														<button className="cancel-button" onClick={onCancel}>
															Cancel
														</button>
														<button
															className="save-button inactive"
															onClick={onSavePriceData}
														>
															Save
														</button>
													</div>
												</div>
											) : (
												<div className="normal-view">
													<h2>${item.price}</h2>
													<p className="unit-info">/unit ({item.priceType})</p>

													<p className="value-info">
														Total value: $
														{item.price *
															getItemTotal(item.colorWiseSizeQuantityPairList)}
													</p>
													<button
														className="edit-button"
														onClick={() => onEditPriceInfo(item)}
													>
														Edit price
													</button>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
			</section>
		</LoaderComponent>
	);
};

export default AllStyles;
